import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const URL = '/payment_plan_settings';

export const PaymentPlanSettingsSchema = z.object({
  allowPaymentPlans: z.boolean(),
});

export type PaymentPlanSettings = z.infer<typeof PaymentPlanSettingsSchema>;

export async function loadPaymentPlanSettingsQueryFn(axiosInstance: Promise<AxiosInstance>) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL);

  const parseResult = PaymentPlanSettingsSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'paymentPlanSettings');
    throw new SilentError();
  }

  return parseResult.data;
}

export function useLoadPaymentPlanSettings(options: QueryOptions<PaymentPlanSettings> = {}) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<PaymentPlanSettings, ApiError>({
    queryKey: ['paymentPlanSettings'],
    queryFn: () => loadPaymentPlanSettingsQueryFn(axiosInstance),
    ...options,
  });

  return addResourceNameToQueryResult<PaymentPlanSettings, ApiError, 'paymentPlanSettings'>(
    'paymentPlanSettings',
    queryResult,
  );
}
